import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Animated,
  Dimensions,
  Easing,
  Image,
  Platform,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from "react-native";
import Svg, { Circle, G } from "react-native-svg";
import { isFactorTv } from "@rnv/renative";
import { getApiBaseUrl } from "../api/client";
import { sessionsApi, EarnedSticker } from "../api/sessions";
import { stubIcon } from "../icons/stubLibrary";
import { optionPictureUrl } from "../icons/optionPicture";
import { useAuthStore } from "../state/authStore";
import { colors } from "../theme";
import { Check, Volume2 } from "lucide-react-native";
import Mascot from "./Mascot";
import useKeydown from "../hooks/useKeydown";

// Web Speech API helper — text-to-speech for the question prompt. Chromium
// 76+ (Tizen/webOS floor) supports `window.speechSynthesis` natively; on
// native iOS/Android phones there's no synth available, so we gracefully
// no-op. The parent toggles this off in Me → Read questions aloud.
function speakText(text: string) {
  if (typeof window === "undefined") return;
  const synth = (window as any).speechSynthesis;
  const Utt = (window as any).SpeechSynthesisUtterance;
  if (!synth || !Utt) return;
  try {
    synth.cancel(); // stop any in-flight utterance
    const u = new Utt(text);
    u.rate = 0.95;
    u.pitch = 1.05;
    u.volume = 1;
    u.lang = "en-US";
    synth.speak(u);
  } catch {
    // Tizen sometimes throws on the first call before voices load; ignore.
  }
}

// Same idiom as ChildHomeScreen / PinGate — branch sizes for phone.
const IS_PHONE = Platform.OS !== "web" && Dimensions.get("window").width < 768;
const IS_TV = !IS_PHONE;

// Backend ships `options` as `[{ label, iconKey?, imageUrl? }]` on new
// rows, `string[]` on legacy rows. The read-time normalizer in
// content.service.ts coerces both, but parents have been seen passing
// either through to us, so be defensive at the boundary.
// `imageUrl` is the parent-uploaded VideoEntity image — points to the
// public binary endpoint (no auth needed, cuid-gated).
type RawOption =
  | string
  | { label: string; iconKey?: string; imageUrl?: string };
type Props = {
  question: {
    id: string;
    prompt: string;
    options: RawOption[] | string;
    correct: number;
    // Optional scene illustration (social-emotional scenarios) shown above
    // the answer tiles. Absolute URL or a data: URI.
    imageUrl?: string | null;
  };
  sessionId: string;
  token: string;
  onClose: () => void;
  // Fires after the answer is persisted, before the overlay fades out.
  // Used by the TV to broadcast `answer_submitted` to the parent web view.
  onAnswered?: (
    questionId: string,
    selectedOption: number,
    correct: boolean,
  ) => void;
};

type NormalizedOption = {
  label: string;
  iconKey?: string;
  imageUrl?: string;
};

function toOptions(raw: RawOption[] | string): NormalizedOption[] {
  const arr: unknown =
    typeof raw === "string"
      ? (() => {
          try {
            return JSON.parse(raw);
          } catch {
            return [];
          }
        })()
      : raw;
  if (!Array.isArray(arr)) return [];
  return arr.map((o) => {
    if (typeof o === "string") return { label: o };
    if (o && typeof o === "object" && "label" in (o as any)) {
      const obj = o as {
        label: unknown;
        iconKey?: unknown;
        imageUrl?: unknown;
      };
      const label = String(obj.label ?? "");
      const iconKey =
        typeof obj.iconKey === "string" && obj.iconKey.length > 0
          ? obj.iconKey
          : undefined;
      const imageUrl =
        typeof obj.imageUrl === "string" && obj.imageUrl.length > 0
          ? obj.imageUrl
          : undefined;
      return { label, iconKey, imageUrl };
    }
    return { label: "" };
  });
}

// ─── parseOption — ported verbatim from kid-question.jsx ──────────────────
const NUM_WORDS: Record<string, number> = {
  one: 1,
  two: 2,
  three: 3,
  four: 4,
  five: 5,
  six: 6,
  seven: 7,
  eight: 8,
  nine: 9,
  ten: 10,
};

const COLOR_HEX: Record<string, string> = {
  red: "#FF6B6B",
  blue: "#5B8DEF",
  yellow: "#FFD86B",
  green: "#7AC74F",
  orange: "#FF8A4C",
  pink: "#F18EB6",
  purple: "#8C7DD9",
  brown: "#A07248",
  white: "#FFFFFF",
  black: "#2E2316",
};

type Hint =
  | { kind: "count"; n: number }
  | { kind: "color"; hex: string }
  | { kind: "letter"; ch: string }
  | { kind: "text" };

function parseOption(text: string): Hint {
  const t = String(text || "")
    .trim()
    .toLowerCase();
  if (NUM_WORDS[t] != null) return { kind: "count", n: NUM_WORDS[t] };
  if (/^\d+$/.test(t))
    return { kind: "count", n: Math.min(10, parseInt(t, 10)) };
  if (COLOR_HEX[t]) return { kind: "color", hex: COLOR_HEX[t] };
  if (/^[a-z]$/.test(t)) return { kind: "letter", ch: text.toUpperCase() };
  return { kind: "text" };
}

// ─── OptionArt — dot-count cookies, color swatches, huge letters ──────────
type ArtSize = { w: number; h: number };

const COUNT_LAYOUTS: Record<number, Array<[number, number]>> = {
  1: [[0, 0]],
  2: [
    [-0.7, 0],
    [0.7, 0],
  ],
  3: [
    [-0.9, 0],
    [0, 0],
    [0.9, 0],
  ],
  4: [
    [-0.7, -0.5],
    [0.7, -0.5],
    [-0.7, 0.5],
    [0.7, 0.5],
  ],
  5: [
    [-0.9, -0.5],
    [0, -0.5],
    [0.9, -0.5],
    [-0.5, 0.6],
    [0.5, 0.6],
  ],
  6: [
    [-0.9, -0.5],
    [0, -0.5],
    [0.9, -0.5],
    [-0.9, 0.5],
    [0, 0.5],
    [0.9, 0.5],
  ],
};

function CountArt({ n, size }: { n: number; size: ArtSize }) {
  const clamped = Math.max(1, Math.min(6, n));
  const layout = COUNT_LAYOUTS[clamped];
  // viewBox is -3 -2 6 4, mirroring the design spec.
  return (
    <Svg
      viewBox="-3 -2 6 4"
      width={size.w}
      height={size.h}
      preserveAspectRatio="xMidYMid meet"
    >
      {layout.map(([x, y], i) => (
        <G key={i} x={x} y={y}>
          <Circle cx={0} cy={0} r={0.74} fill="#C58850" />
          <Circle cx={0} cy={0} r={0.6} fill="#E1A668" />
          <Circle cx={-0.18} cy={-0.18} r={0.1} fill="#5b3a1a" />
          <Circle cx={0.2} cy={-0.04} r={0.1} fill="#5b3a1a" />
          <Circle cx={-0.1} cy={0.22} r={0.1} fill="#5b3a1a" />
        </G>
      ))}
    </Svg>
  );
}

function ColorArt({ hex, size }: { hex: string; size: ArtSize }) {
  // 72% of the tile's art slot, as a flat circle. RN `View` with
  // borderRadius:999 renders fine on engine-rn-web + native.
  const d = Math.floor(Math.min(size.w, size.h) * 0.72);
  const isWhite = hex.toUpperCase() === "#FFFFFF";
  return (
    <View
      style={{
        width: d,
        height: d,
        borderRadius: 999,
        backgroundColor: hex,
        borderWidth: isWhite ? 3 : 0,
        borderColor: colors.lineStrong,
        // shadow keeps the swatch from floating on the cream bg
        shadowColor: "#000",
        shadowOffset: { width: 0, height: 6 },
        shadowOpacity: 0.1,
        shadowRadius: 0,
        elevation: 3,
      }}
    />
  );
}

function LetterArt({
  ch,
  fontSize,
  color,
}: {
  ch: string;
  fontSize: number;
  color: string;
}) {
  return (
    <Text
      style={{
        fontSize,
        lineHeight: Math.round(fontSize * 0.95),
        fontWeight: "900",
        color,
        textAlign: "center",
        // RN-web supports textShadow*; native ignores it gracefully.
        textShadowColor: "rgba(180,90,36,0.12)",
        textShadowOffset: { width: 0, height: 6 },
        textShadowRadius: 0,
      }}
    >
      {ch}
    </Text>
  );
}

function OptionArt({
  hint,
  tile,
  labelColor,
}: {
  hint: Hint;
  tile: number;
  labelColor: string;
}) {
  // Art slot is ~60% of the tile height (we reserve the bottom for the word).
  const slot = Math.round(tile * 0.6);
  if (hint.kind === "count") {
    return <CountArt n={hint.n} size={{ w: slot * 1.4, h: slot }} />;
  }
  if (hint.kind === "color") {
    return <ColorArt hex={hint.hex} size={{ w: slot, h: slot }} />;
  }
  if (hint.kind === "letter") {
    // Cap to slot height so the letter never overflows the tile.
    const fontSize = Math.min(IS_TV ? 120 : 96, Math.round(slot * 1.05));
    return (
      <LetterArt ch={hint.ch} fontSize={fontSize} color={colors.primary} />
    );
  }
  // Plain text fallback rendered by the tile itself.
  return null;
}

// ─── State helpers ────────────────────────────────────────────────────────
type TileVisual = {
  bg: string;
  ring: string;
  label: string;
  showRing: boolean;
};

function tileVisual(args: {
  resolved: boolean;
  selected: number | null;
  correctIndex: number;
  index: number;
}): TileVisual {
  const { resolved, selected, correctIndex, index } = args;
  const isCorrect = index === correctIndex;
  const isPicked = selected !== null && index === selected;
  if (resolved) {
    if (isCorrect) {
      return {
        bg: "#E6F8DA",
        ring: "#7AC74F",
        label: "#2F5A1E",
        showRing: true,
      };
    }
    if (isPicked) {
      return {
        bg: "#FFE6E6",
        ring: "#FF7A7A",
        label: "#7A2E2E",
        showRing: true,
      };
    }
    return {
      bg: "rgba(0,0,0,0.04)",
      ring: "transparent",
      label: colors.ink3,
      showRing: false,
    };
  }
  if (isPicked) {
    return {
      bg: "#FFE2C2",
      ring: colors.primary,
      label: colors.ink,
      showRing: true,
    };
  }
  return {
    bg: "#FFF7EC",
    ring: "rgba(46,35,22,0.10)",
    label: colors.ink,
    showRing: true,
  };
}

// ─── Component ────────────────────────────────────────────────────────────
export default function QuestionOverlay({
  question,
  sessionId,
  token,
  onClose,
  onAnswered,
}: Props) {
  const { activeChild, ttsEnabled } = useAuthStore();
  const mascotColor = activeChild?.color || colors.primary;

  const options = useMemo(
    () => toOptions(question.options),
    [question.options],
  );
  const hints = useMemo(
    () => options.map((o) => parseOption(o.label)),
    [options],
  );
  // Every answer is a real picture (frame / uploaded / scene image)? Then this
  // is a pre-reader, pictures-only question — drop the white question card and
  // the tile chrome and let the bare images stand on their own.
  const allImages = useMemo(
    () => options.length > 0 && options.every((o) => !!o.imageUrl),
    [options],
  );

  const [selected, setSelected] = useState<number | null>(null);
  const [resolved, setResolved] = useState(false);
  const [correct, setCorrect] = useState(false);
  // Stickers unlocked by this answer (returned by the backend). When set, we
  // show a celebratory unlock card; auto-clears so it doesn't linger.
  const [unlocked, setUnlocked] = useState<EarnedSticker[]>([]);
  // Remote / keyboard focus index. Starts on the first option so a TV user
  // sees something highlighted the moment the overlay appears.
  const [focused, setFocused] = useState(0);

  // Card fade-up entrance.
  const enter = useRef(new Animated.Value(0)).current;
  useEffect(() => {
    Animated.timing(enter, {
      toValue: 1,
      duration: 320,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: true,
    }).start();
  }, [enter]);

  // Mascot bob.
  const bob = useRef(new Animated.Value(0)).current;
  useEffect(() => {
    const anim = Animated.loop(
      Animated.sequence([
        Animated.timing(bob, {
          toValue: -6,
          duration: 1200,
          easing: Easing.inOut(Easing.ease),
          useNativeDriver: true,
        }),
        Animated.timing(bob, {
          toValue: 0,
          duration: 1200,
          easing: Easing.inOut(Easing.ease),
          useNativeDriver: true,
        }),
      ]),
    );
    anim.start();
    return () => anim.stop();
  }, [bob]);

  // Auto-close ~2.2s after resolution.
  useEffect(() => {
    if (!resolved) return;
    const timer = setTimeout(onClose, 2200);
    return () => clearTimeout(timer);
  }, [resolved, onClose]);

  // Read the prompt aloud when the overlay first appears, gated on the
  // parent's Me → "Read questions aloud" setting. Re-runs only if the
  // question id changes (a new question), not on selection/resolution.
  // We stop synthesis on unmount so a quickly-dismissed overlay doesn't
  // keep talking into silence.
  useEffect(() => {
    if (!ttsEnabled) return;
    speakText(question.prompt);
    return () => {
      if (typeof window !== "undefined") {
        const synth = (window as any).speechSynthesis;
        synth?.cancel?.();
      }
    };
  }, [question.id, question.prompt, ttsEnabled]);

  const handleTap = async (index: number) => {
    if (resolved || selected !== null) return;
    setSelected(index);
    try {
      const result = await sessionsApi.submitAnswer(
        sessionId,
        question.id,
        index,
        token,
      );
      const wasCorrect = result.correct;
      setCorrect(wasCorrect);
      setResolved(true);
      if (result.newStickers && result.newStickers.length > 0) {
        setUnlocked(result.newStickers);
      }
      onAnswered?.(question.id, index, wasCorrect);
    } catch {
      // Network/server hiccup: still resolve locally using the index-equality
      // rule so the kid isn't left staring at a frozen overlay.
      const wasCorrect = index === question.correct;
      setCorrect(wasCorrect);
      setResolved(true);
      onAnswered?.(question.id, index, wasCorrect);
    }
  };

  const cols = options.length === 4 ? 2 : options.length;
  // Image-only answers render as a cross/diamond (top · right · bottom · left)
  // rather than a grid. Count-adaptive so there's never an empty slot.
  const isDiamond =
    isFactorTv && allImages && options.length >= 2 && options.length <= 4;
  const diamondDir: Record<"up" | "down" | "left" | "right", number | null> =
    options.length >= 4
      ? { up: 0, right: 1, down: 2, left: 3 }
      : options.length === 3
      ? { up: 0, left: 1, right: 2, down: null }
      : { up: null, down: null, left: 0, right: 1 };

  // TV / keyboard navigation. We hoist this above the render branch so the
  // hook order is stable, and gate with `isActive: IS_TV` so phone keeps
  // its plain-tap behavior (no spurious keyboard hijack if a Bluetooth
  // keyboard is paired). Arrow keys move within the grid; OK submits;
  // Back closes the overlay so the kid isn't stranded if they bail.
  useKeydown({
    isActive: IS_TV,
    left: () => {
      if (resolved) return;
      if (isDiamond) {
        setFocused((f) => diamondDir.left ?? f);
        return;
      }
      setFocused((f) => {
        const row = Math.floor(f / cols);
        const col = f % cols;
        const nextCol = Math.max(0, col - 1);
        return Math.min(options.length - 1, row * cols + nextCol);
      });
    },
    right: () => {
      if (resolved) return;
      if (isDiamond) {
        setFocused((f) => diamondDir.right ?? f);
        return;
      }
      setFocused((f) => {
        const row = Math.floor(f / cols);
        const col = f % cols;
        // Clamp to the actual last column in this row, not just `cols - 1`,
        // so an odd option count (3 in one row) doesn't let focus fall on
        // an empty cell.
        const lastColInRow = Math.min(
          cols - 1,
          options.length - 1 - row * cols,
        );
        const nextCol = Math.min(lastColInRow, col + 1);
        return row * cols + nextCol;
      });
    },
    up: () => {
      if (resolved) return;
      if (isDiamond) {
        setFocused((f) => diamondDir.up ?? f);
        return;
      }
      setFocused((f) => {
        const row = Math.floor(f / cols);
        const col = f % cols;
        if (row === 0) return f;
        return (row - 1) * cols + col;
      });
    },
    down: () => {
      if (resolved) return;
      if (isDiamond) {
        setFocused((f) => diamondDir.down ?? f);
        return;
      }
      setFocused((f) => {
        const row = Math.floor(f / cols);
        const col = f % cols;
        const nextIdx = (row + 1) * cols + col;
        return nextIdx < options.length ? nextIdx : f;
      });
    },
    ok: () => {
      if (resolved || selected !== null) return;
      handleTap(focused);
    },
    back: () => {
      // Bail out cleanly — same as the auto-close timer at the end of a
      // resolved answer, but here triggered by the user.
      onClose();
    },
  });

  const diamondTile = IS_TV ? 200 : 116;
  const tile = isDiamond ? diamondTile : IS_TV ? 210 : 146;
  const tileGap = IS_TV ? 16 : 12;
  // Diamond geometry: a 3×3 cell grid (N/E/S/W occupied, center empty). The
  // container is 3 tiles wide; its height collapses for 2/3-option counts.
  const diamondS = diamondTile * 3;
  const diamondH =
    options.length >= 4
      ? diamondTile * 3
      : options.length === 3
      ? diamondTile * 2
      : diamondTile;
  const diamondTileStyle = (i: number): any => {
    const n = options.length;
    const slot =
      n >= 4
        ? ["top", "right", "bottom", "left"][i]
        : n === 3
        ? ["top", "left", "right"][i]
        : ["left", "right"][i];
    const midX = diamondTile; // centered third (horizontal)
    const sideY = n === 2 ? 0 : diamondTile; // left/right vertical offset
    const base = { position: "absolute", marginRight: 0, marginBottom: 0 };
    switch (slot) {
      case "top":
        return { ...base, top: 0, left: midX };
      case "bottom":
        return { ...base, bottom: 0, left: midX };
      case "left":
        return { ...base, left: 0, top: sideY };
      case "right":
        return { ...base, right: 0, top: sideY };
      default:
        return base;
    }
  };
  const promptSize = IS_TV ? 44 : 28;
  const cardMaxW = IS_TV ? 880 : 720;
  const cardPadH = IS_TV ? 36 : 28;
  const cardPadV = IS_TV ? 32 : 24;
  // The replay speaker only exists where Web Speech does (Tizen/webOS web); on
  // native phones it never renders, so don't reserve prompt padding for it —
  // that wasted space is what squeezed the prompt into too many lines.
  const hasSpeaker =
    ttsEnabled &&
    typeof window !== "undefined" &&
    !!(window as any).speechSynthesis;

  // We build a wrapping row of tiles using marginRight (no flex `gap`).
  // To keep a clean grid layout across 2/3/4 options we set the container
  // width to `cols * tile + (cols - 1) * gap` and let it self-center.
  const rowWidth = cols * tile + (cols - 1) * tileGap;

  const translateY = enter.interpolate({
    inputRange: [0, 1],
    outputRange: [16, 0],
  });

  return (
    <View style={styles.overlay} pointerEvents="box-none">
      <View style={styles.backdrop} pointerEvents="auto" />
      <Animated.View
        style={[
          styles.card,
          {
            maxWidth: cardMaxW,
            paddingHorizontal: cardPadH,
            paddingTop: cardPadV,
            paddingBottom: cardPadV + 4,
            opacity: enter,
            transform: [{ translateY }],
          },
          // Pictures-only question → no card chrome, just the floating images.
          allImages && {
            backgroundColor: "transparent",
            borderWidth: 0,
            paddingHorizontal: 0,
            paddingTop: 0,
            paddingBottom: 0,
            shadowOpacity: 0,
            elevation: 0,
          },
        ]}
      >
        {/* MASCOT + SPEECH BUBBLE — shown only when "Speak the question" is on.
            When it's off, the kid sees the answer pictures and nothing else
            (no prompt text, no mascot, no audio — auto-speak is gated too). */}
        {ttsEnabled && (
        <View style={styles.topRow}>
          <Animated.View
            style={[styles.mascotWrap, { transform: [{ translateY: bob }] }]}
          >
            <Mascot
              mood="curious"
              color={mascotColor}
              size={IS_TV ? 120 : 72}
            />
          </Animated.View>
          <View
            style={[
              styles.bubble,
              {
                paddingHorizontal: IS_TV ? 26 : 18,
                paddingVertical: IS_TV ? 20 : 14,
              },
            ]}
          >
            {/* speech-bubble tail: peeks out from the mascot side */}
            <View
              style={IS_TV ? styles.bubbleTailLeft : styles.bubbleTailTop}
            />
            <Text
              style={[
                styles.prompt,
                {
                  fontSize: promptSize,
                  lineHeight: Math.round(promptSize * 1.1),
                  textAlign: IS_TV ? "left" : "center",
                  // Reserve room on the right for the speaker button only when
                  // it actually renders — otherwise the prompt gets the full
                  // width and wraps to fewer lines.
                  paddingRight: hasSpeaker ? (IS_TV ? 76 : 56) : 0,
                },
              ]}
            >
              {question.prompt}
            </Text>
            {/* Replay button — only renders when TTS is enabled. Tapping it
                re-speaks the prompt; mounting also auto-speaks via the
                effect above. Suppressed entirely on platforms without
                speechSynthesis so there's no dead button to mash. */}
            {hasSpeaker && (
                <TouchableOpacity
                  style={[
                    styles.speakerBtn,
                    {
                      width: IS_TV ? 56 : 44,
                      height: IS_TV ? 56 : 44,
                      borderRadius: IS_TV ? 28 : 22,
                    },
                  ]}
                  onPress={() => speakText(question.prompt)}
                  activeOpacity={0.85}
                  accessibilityLabel="Read aloud"
                >
                  <Volume2
                    size={IS_TV ? 24 : 18}
                    color="#fff"
                    strokeWidth={2.4}
                  />
                </TouchableOpacity>
              )}
          </View>
        </View>
        )}

        {/* OPTION TILES ──────────────────────────────────────────────── */}
        <View style={[styles.tileGridOuter, { marginTop: IS_TV ? 26 : 18 }]}>
          <View
            style={[
              styles.tileGrid,
              isDiamond
                ? { width: diamondS, height: diamondH, position: "relative" }
                : { width: rowWidth },
            ]}
          >
            {options.map((opt, i) => {
              const hint = hints[i];
              const icon = stubIcon(opt.iconKey);
              const vis = tileVisual({
                resolved,
                selected,
                correctIndex: question.correct,
                index: i,
              });
              const isLastInRow = (i + 1) % cols === 0;
              const isLastRow = i >= options.length - cols;
              // Three-way precedence for the tile visual:
              //   1) parent-uploaded image (option.imageUrl from VideoEntity)
              //   2) shared icon library (option.iconKey → stub icon)
              //   3) procedural auto-detection (count / color / letter / text)
              // The word label still renders in all non-text cases — it
              // helps emerging readers map picture to word.
              const optionImageSrc = opt.imageUrl
                ? /^(https?:|data:)/.test(opt.imageUrl)
                  ? opt.imageUrl
                  : `${getApiBaseUrl()}${opt.imageUrl}`
                : null;
              const hasImage = !!optionImageSrc;
              const hasIcon = !hasImage && !!icon;
              // Pre-readers can't read a word-answer — map plain words to a
              // picture so every tile is answerable without reading.
              const picUrl =
                !hasImage && !hasIcon && hint.kind === "text"
                  ? optionPictureUrl(opt.label)
                  : null;
              // TV focus state: a thick primary-color ring + small lift so
              // the remote-driven selection is unmistakable. Suppressed
              // once the answer is resolved (the result ring takes over).
              const isFocused = isFactorTv && !resolved && focused === i;
              return (
                <TouchableOpacity
                  key={i}
                  activeOpacity={resolved ? 1 : 0.85}
                  disabled={resolved}
                  onPress={() => handleTap(i)}
                  // Mirror the same focus highlight when the kid hovers
                  // with a mouse/Magic Remote pointer — keeps mouse and
                  // arrow-keys consistent on web/TV. `onMouseEnter` is a
                  // web-only prop react-native-web forwards to the DOM, but
                  // RN's TouchableOpacity types omit it — spread via `any`.
                  {...(IS_TV && !resolved
                    ? ({ onMouseEnter: () => setFocused(i) } as any)
                    : {})}
                  style={[
                    styles.tile,
                    {
                      width: tile,
                      height: tile,
                      backgroundColor: vis.bg,
                      // Inset 4px ring via borderWidth — no inset shadow needed,
                      // which `react-native-web` on old Chromium doesn't render.
                      // When focused (TV/remote), override to a thicker
                      // primary ring so it dominates the per-tile state ring.
                      borderWidth: isFocused ? 6 : 4,
                      borderColor: isFocused
                        ? colors.primary
                        : vis.showRing
                        ? vis.ring
                        : "transparent",
                      marginRight: isLastInRow ? 0 : tileGap,
                      marginBottom: isLastRow ? 0 : tileGap,
                      opacity: resolved && !vis.showRing ? 0.85 : 1,
                      // Subtle lift to reinforce the focus state — Chromium
                      // 76 (Tizen/webOS floor) supports `transform`.
                      transform: isFocused ? [{ translateY: -4 }] : [],
                      // Glow shadow when focused. RN-Web maps these to
                      // box-shadow which Chromium 76 supports.
                      shadowColor: isFocused ? colors.primary : "#000",
                      shadowOffset: isFocused
                        ? { width: 0, height: 12 }
                        : { width: 0, height: 0 },
                      shadowOpacity: isFocused ? 0.35 : 0,
                      shadowRadius: isFocused ? 22 : 0,
                      elevation: isFocused ? 10 : 0,
                    },
                    // Pictures-only: bare image (no cream tile), rounded, with a
                    // soft drop shadow for separation; a ring draws only on
                    // focus or answer feedback.
                    allImages && {
                      backgroundColor: "transparent",
                      padding: 0,
                      borderWidth: isFocused ? 6 : vis.showRing ? 5 : 0,
                      borderColor: isFocused
                        ? colors.primary
                        : vis.showRing
                        ? vis.ring
                        : "transparent",
                      shadowColor: isFocused ? colors.primary : "#000",
                      shadowOffset: { width: 0, height: isFocused ? 12 : 8 },
                      shadowOpacity: isFocused ? 0.4 : 0.32,
                      shadowRadius: isFocused ? 22 : 16,
                      elevation: isFocused ? 12 : 8,
                    },
                    // Image-only: absolutely position this tile into its
                    // cardinal slot (top/right/bottom/left) of the diamond.
                    isDiamond && diamondTileStyle(i),
                  ]}
                >
                  <View style={styles.tileArtSlot}>
                    {hasImage ? (
                      <Image
                        source={{ uri: optionImageSrc! }}
                        style={
                          allImages
                            ? { width: "100%", height: "100%", borderRadius: 18 }
                            : { width: "88%", height: "88%" }
                        }
                        resizeMode={allImages ? "cover" : "contain"}
                      />
                    ) : hasIcon ? (
                      <Image
                        source={{ uri: icon!.imageUrl }}
                        style={{ width: "78%", height: "78%" }}
                        resizeMode="contain"
                      />
                    ) : hint.kind !== "text" ? (
                      <OptionArt
                        hint={hint}
                        tile={tile}
                        labelColor={vis.label}
                      />
                    ) : picUrl ? (
                      <Image
                        source={{ uri: picUrl }}
                        style={{ width: "78%", height: "78%" }}
                        resizeMode="contain"
                      />
                    ) : (
                      <Text
                        style={[
                          styles.tileTextBig,
                          { color: vis.label, fontSize: IS_TV ? 40 : 28 },
                        ]}
                        numberOfLines={3}
                      >
                        {opt.label}
                      </Text>
                    )}
                  </View>

                  {(hasIcon || hint.kind !== "text" || picUrl) && (
                    <Text
                      style={[
                        styles.tileLabel,
                        { color: vis.label, fontSize: IS_TV ? 26 : 20 },
                      ]}
                      numberOfLines={1}
                    >
                      {opt.label}
                    </Text>
                  )}

                  {/* result badge */}
                  {resolved && i === question.correct && (
                    <View
                      style={[styles.badge, { backgroundColor: "#7AC74F" }]}
                    >
                      <Check size={14} color="#fff" strokeWidth={2.5} />
                    </View>
                  )}
                  {resolved && selected === i && i !== question.correct && (
                    <View
                      style={[styles.badge, { backgroundColor: "#FF7A7A" }]}
                    >
                      <Text style={styles.badgeMark}>{"×"}</Text>
                    </View>
                  )}
                </TouchableOpacity>
              );
            })}
          </View>
        </View>

        {/* FEEDBACK ROW ─────────────────────────────────────────────── */}
        {resolved && (
          <View style={[styles.feedbackRow, { marginTop: IS_TV ? 22 : 18 }]}>
            <View style={styles.feedbackMascot}>
              <Mascot
                mood={correct ? "cheer" : "happy"}
                color={colors.primary}
                size={correct ? (IS_TV ? 64 : 52) : IS_TV ? 56 : 48}
              />
            </View>
            <Text
              style={[
                styles.feedbackText,
                {
                  color: correct ? "#3A8530" : colors.ink2,
                  fontSize: IS_TV ? 28 : 22,
                },
              ]}
            >
              {correct ? "Nice! ⭐ +1 star" : "Almost — keep watching!"}
            </Text>
          </View>
        )}

        {/* STICKER UNLOCK ───────────────────────────────────────────────
            Shown when the answer just earned one or more stickers. Kid-facing
            celebration; the parent sees the full book in the web app. */}
        {unlocked.length > 0 && (
          <View style={styles.unlockCard}>
            <Text style={styles.unlockEmoji}>{unlocked[0].emoji}</Text>
            <View style={{ flex: 1 }}>
              <Text style={styles.unlockEyebrow}>NEW STICKER!</Text>
              <Text style={styles.unlockTitle}>
                {unlocked.length === 1
                  ? unlocked[0].title
                  : `${unlocked[0].title} +${unlocked.length - 1} more`}
              </Text>
            </View>
          </View>
        )}
      </Animated.View>
    </View>
  );
}

const styles = StyleSheet.create({
  overlay: {
    ...StyleSheet.absoluteFillObject,
    alignItems: "center",
    justifyContent: "center",
    zIndex: 100,
    padding: IS_TV ? 60 : 36,
  },
  unlockCard: {
    flexDirection: "row",
    alignItems: "center",
    alignSelf: "center",
    // No flex `gap`: this overlay runs on the TVs, and Tizen/WebOS sit on a
    // Chromium 76 floor that ignores it (needs 84+). The spacing lives on
    // `unlockEmoji`'s marginRight instead.
    marginTop: 14,
    marginHorizontal: IS_TV ? 28 : 18,
    marginBottom: IS_TV ? 22 : 16,
    paddingVertical: IS_TV ? 14 : 12,
    paddingHorizontal: IS_TV ? 22 : 18,
    borderRadius: 20,
    backgroundColor: "#FFF6E6",
    borderWidth: 2,
    borderColor: colors.primary,
  },
  unlockEmoji: {
    marginRight: 14,
    fontSize: IS_TV ? 44 : 36,
  },
  unlockEyebrow: {
    fontSize: IS_TV ? 13 : 11,
    fontWeight: "900",
    letterSpacing: 1.2,
    color: colors.primary,
    marginBottom: 2,
  },
  unlockTitle: {
    fontSize: IS_TV ? 24 : 19,
    fontWeight: "900",
    color: colors.ink,
  },
  backdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: "rgba(0,0,0,0.45)",
  },
  card: {
    backgroundColor: colors.surface,
    borderRadius: 32,
    borderWidth: 4,
    borderColor: colors.ink,
    width: "100%",
    // Heavy shadow per the design spec.
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 30 },
    shadowOpacity: 0.4,
    shadowRadius: 60,
    elevation: 16,
  },
  topRow: {
    flexDirection: IS_TV ? "row" : "column",
    alignItems: "center",
  },
  mascotWrap: {
    // No flex `gap` — we use marginRight/marginBottom on the mascot wrapper
    // to space the bubble away from it (Chromium <84 floor; same pattern
    // as PinGate). On phone the topRow is a column, so use marginBottom.
    marginRight: IS_TV ? 22 : 0,
    marginBottom: IS_TV ? 0 : 10,
  },
  bubble: {
    flex: IS_TV ? 1 : undefined,
    alignSelf: IS_TV ? "auto" : "stretch",
    backgroundColor: colors.bgWarm,
    borderRadius: 22,
    position: "relative",
  },
  bubbleTailLeft: {
    position: "absolute",
    left: -10,
    top: "50%",
    width: 20,
    height: 20,
    marginTop: -10, // center the rotated square on the bubble's mid-line
    backgroundColor: colors.bgWarm,
    borderRadius: 4,
    transform: [{ rotate: "45deg" }],
  },
  bubbleTailTop: {
    position: "absolute",
    top: -10,
    left: "50%",
    width: 20,
    height: 20,
    marginLeft: -10,
    backgroundColor: colors.bgWarm,
    borderRadius: 4,
    transform: [{ rotate: "45deg" }],
  },
  prompt: {
    fontWeight: "900",
    color: colors.ink,
    letterSpacing: -0.3,
  },
  // Floating speaker icon on the speech bubble — bottom-right of the
  // bubble box so it tracks the prompt regardless of how long the text is.
  // Uses primary fill for instant recognition as a "tap me" affordance.
  speakerBtn: {
    position: "absolute",
    right: 12,
    bottom: 12,
    backgroundColor: colors.primary,
    alignItems: "center",
    justifyContent: "center",
    shadowColor: colors.primary,
    shadowOffset: { width: 0, height: 6 },
    shadowOpacity: 0.35,
    shadowRadius: 12,
    elevation: 6,
  },
  tileGridOuter: {
    alignItems: "center",
  },
  tileGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    // No `gap` — `marginRight` / `marginBottom` are applied per-child below.
  },
  tile: {
    borderRadius: 24,
    padding: 16,
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
    overflow: "hidden",
    backgroundColor: "#FFF7EC",
  },
  tileArtSlot: {
    flex: 1,
    width: "100%",
    alignItems: "center",
    justifyContent: "center",
  },
  tileTextBig: {
    fontWeight: "900",
    textAlign: "center",
    letterSpacing: -0.3,
  },
  tileLabel: {
    fontWeight: "900",
    letterSpacing: -0.2,
    marginTop: 6,
    textAlign: "center",
  },
  badge: {
    position: "absolute",
    top: 10,
    right: 10,
    width: 36,
    height: 36,
    borderRadius: 18,
    alignItems: "center",
    justifyContent: "center",
  },
  badgeMark: {
    color: "#fff",
    fontSize: 22,
    fontWeight: "900",
    lineHeight: 24,
  },
  feedbackRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
  },
  feedbackMascot: {
    marginRight: 14, // gap replacement
  },
  feedbackText: {
    fontWeight: "900",
    letterSpacing: -0.2,
  },
});
